import React, {
    createRef,
    FunctionComponent,
    Suspense,
    useEffect,
    useState,
} from 'react';
import { ReactComponentElement } from 'react';
import { Cell, Grid } from 'baseui/layout-grid';
import { Block } from 'baseui/block';
import { RoutingBreadcrumbs } from '../RoutingBreadCrumbs/RoutingBreadcrumbs';
import { AppDialog } from '../AppDialog/AppDialog';
import { AppNotifications } from '../AppNotifications/AppNotifications';
import { useStyletron } from 'baseui';
import { LinearLoader } from '../LinearLoader/LinearLoader';
import { SuspenseStateTransmitter } from '../SuspenseStateTransmitter/SuspenseStateTransmitter';
import { AsyncMountLoader } from '../PageLoader/AsyncMountLoader';
import { useMediaQuery } from '../../shared/hooks/useMediaQuery';

interface LayoutProps {
    children: ReactComponentElement<any> | Array<ReactComponentElement<any>>;
    menu?: ReactComponentElement<any> | Array<ReactComponentElement<any>>;
    navBar?: ReactComponentElement<any> | Array<ReactComponentElement<any>>;
    progressBarVisible?: boolean;
    progressBarLoading?: boolean;
}

/*
Code in this component, especially related to CSSTransition is connected to index.css
 */
export const Layout: FunctionComponent<LayoutProps> = ({
                                                           children,
                                                           menu,
                                                           navBar,
                                                           progressBarVisible,
                                                           progressBarLoading,
                                                       }) => {
    const [navBarHeight, setNavBarHeight] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const ref = createRef<HTMLDivElement>();
    const [css, theme] = useStyletron();
    const shouldShowDesktopMenu = useMediaQuery(theme.mediaQuery.large.replace('@media ',''));

    const containerCss = css({
        position: 'relative',
    });

    useEffect(() => {
        if (!ref.current) return;

        const { height } = ref.current.getBoundingClientRect();

        setNavBarHeight(height);
    }, [ref]);

    return (
        <main>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
                ref={ref}
            >
                {navBar}
                {progressBarVisible && (
                    <LinearLoader loading={loading || !!progressBarLoading}/>
                )}
            </div>
            <Grid>
                {
                    shouldShowDesktopMenu && (
                        <Cell span={[3]}>
                            <aside
                                style={{
                                    position: 'sticky',
                                    top: navBarHeight,
                                }}
                            >
                                <Block paddingTop={['35px']} paddingBottom={['20px']}>
                                    {menu}
                                </Block>
                            </aside>
                        </Cell>
                    )
                }
                <Cell span={[9]}>
                    <Block paddingTop={['35px']} paddingBottom={['20px']}>
                        <div className={containerCss}>
                            <AppDialog/>
                            <AsyncMountLoader entity={loading} showLoaderContent={false}>
                                <Suspense
                                    fallback={
                                        <SuspenseStateTransmitter
                                            setLoading={setLoading}
                                        />
                                    }
                                >
                                    <div>
                                        <RoutingBreadcrumbs/>
                                        <Block
                                            paddingTop={['0px']}
                                            paddingBottom={['20px']}
                                        >
                                            {children}
                                        </Block>
                                    </div>
                                </Suspense>
                            </AsyncMountLoader>
                            <AppNotifications/>
                        </div>
                    </Block>
                </Cell>
            </Grid>
        </main>
    );
};
